import { useEffect, useRef } from 'react';
import type { ShouldRevalidateFunction } from 'react-router';
import type BoltMetaArgs from 'types/BoltMetaArgs';
import type { CapiProductPage } from 'types/capi/pdp';

import Breadcrumbs from '~/components/BreadCrumbs';
import DownloadAppBlock from '~/components/DownloadAppBlock';
import HorizontalLine from '~/components/HorizontalLine';
import PageLoadErrorBoundary from '~/components/PageLoadErrorBoundary';
import RecommendedArticles from '~/components/RecommendedArticles';
import PdpViewEvent from '~/events/PdpViewEvent';
import usePageData from '~/hooks/usePageData';
import usePageViewEvent from '~/hooks/usePageViewEvent';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import { getAlternateTagsForRegions } from '~/utils/alternateTags';
import seo from '~/utils/seo';

import { Carousel } from '../components/Carousel';
import ProductDetails from '../components/ProductDetails/ProductDetails';
import ProductDetailsTruncated from '../components/ProductDetails/ProductDetailsTruncated';
import ProductInformation from '../components/ProductDetails/ProductInformation';
import ProductMetaData from '../components/ProductMetaData';
import SocialSection from '../components/SocialSection';
import Banner from './Banner';
import MoreColours from './MoreColours';
import ProductCardCarousels from './ProductCardCarousels/ProductCardCarousels';
import Rating from './Rating';
import RelatedCategories from './RelatedCategories';
import WhereToBuyComingSoon from './WhereToBuyComingSoon';
import WhereToBuyInStock from './WhereToBuyInStock';

export default function ProductPage() {
  const { clientIp, isBot } = useRootData();
  const user = useUser();
  const product = usePageData<CapiProductPage>();

  useStructuredData(product._schemas);
  usePageViewEvent('pdp');

  const userEventData = useRef({
    clientIp,
    isBot,
    user
  });

  useEffect(() => {
    new PdpViewEvent({ ...userEventData.current, product }).send();
  }, [product]);

  return (
    <div key={product.product_id}>
      <Banner />
      <div className="mx-auto max-w-desktop px-4 pt-4 md:px-8 desktop:px-0">
        <div className="hidden pb-8 pt-4 md:flex">
          <Breadcrumbs breadcrumbs={product.breadcrumbs} />
        </div>

        <div className="flex flex-col gap-8">
          <div className="md:grid md:grid-cols-6 lg:grid-cols-12 lg:gap-6">
            <div className="md:col-span-6 lg:col-span-8 xl:col-span-8">
              <Carousel />
              <div className="my-4 md:mt-10">
                <ProductMetaData />
              </div>
              {product.rating?.count && product.rating?.count > 0 && (
                <div className="mb-4">
                  <Rating />
                </div>
              )}
              <div className="mt-2">
                <MoreColours />
              </div>
              <div className="lg:hidden">
                <ProductDetailsTruncated />
              </div>
              <div className="hidden lg:block">
                <ProductDetails />
              </div>
            </div>
            {['coming-soon', 'tbc'].includes(product.stock_status) ? (
              <WhereToBuyComingSoon />
            ) : (
              <WhereToBuyInStock />
            )}
          </div>
          <div className="block lg:hidden">
            <ProductInformation />
          </div>
          <SocialSection />
          <RelatedCategories />
          <HorizontalLine />
        </div>
      </div>
      <div className="pt-12">
        <ProductCardCarousels />
        <DownloadAppBlock
          downloadLink="https://thesolesupplier.onelink.me/BPkz/m35evxoc"
          pagePosition="Body"
          qrCode="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_pdp_qr_code.png"
        />
        {product.recent_articles.length > 0 && (
          <>
            <hr className="w-full border-grey-midnight-read"></hr>
            <div className="bg-concrete">
              <RecommendedArticles
                articles={product.recent_articles}
                viewMoreNewsPath={product.view_more_news_path}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <PageLoadErrorBoundary pageType="pdp" />;
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt/login')) return true;
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: CapiProductPage;
};

export const handle = { i18n: ['pdp'] };

export { loader } from './loader.server';

export const meta = (args: BoltMetaArgs<LoaderData>) => {
  const { data, location } = args;

  const meta = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  if (location.search.includes('variant=')) return seo.addMetaNoIndex(meta);
  if (location.search.includes('raffle=')) return seo.addMetaNoIndex(meta);

  return meta.concat(
    getAlternateTagsForRegions(args, data?.pageData.published_region_codes)
  );
};
